<template>
  <LcFormFieldOriginal
    ref="formField"
    v-bind="{ ...props, ...$attrs }"
    @focusin="trackFocusIn"
    @focusout="trackFocusOut"
    @pointerdown="handleAutomaticFocus"
  >
    <template v-for="(_, slotName) in slots" #[slotName]="slotData">
      <slot v-bind="normalizedSlotData(slotData)" :name="slotName" />
    </template>
  </LcFormFieldOriginal>
</template>

<script lang="ts" setup>
import type { ComponentExposed } from 'vue-component-type-helpers'

import {
  LcFormField as LcFormFieldOriginal,
  type LcFormFieldProps,
  type LcFormFieldSlots,
} from '@lottocom/frontend-components'

import { LcFormContext } from '../../app/types/trackingComponents'

const props = defineProps<LcFormFieldProps>()
const slots = defineSlots<LcFormFieldSlots>()

const { pushFormInputEvent } = useTracking()
const { log } = useDatadog()

const { getIsAutomaticFocus, notifyAboutFocusIn, setNextFocusOn, setTabListener } =
  useTrackingFieldSingleton()

const isFirstFocusIn = ref(true)
const fieldRef = useTemplateRef<ComponentExposed<typeof LcFormFieldOriginal>>('formField')
const context = inject(LcFormContext, null)

const getError = () => fieldRef.value?.field.errorMessage

const getValue = () => {
  const value = fieldRef.value?.field.value
  return value ? 'pii_redacted' : ''
}

const handleAutomaticFocus = (event: PointerEvent) => {
  const clickName = getClickNameOfTarget(event)
  setNextFocusOn(clickName)
}

const trackFocusIn = async (event: FocusEvent) => {
  try {
    await waitMs(10)
    const clickName = getClickNameOfTarget(event)

    pushFormInputEvent({
      finput_action: 'input_start',
      finput_automatic_focus: getIsAutomaticFocus(clickName),
      finput_data_clickname: clickName,
      finput_data_container: context?.containerId.value,
      finput_firstfocusin: isFirstFocusIn.value,
      finput_value: getValue(),
    })
  } catch (error) {
    log('warn', {
      component: 'LcFormField.vue',
      error,
      message: 'pushFormInputEvent failed',
    })
  } finally {
    isFirstFocusIn.value = false
    notifyAboutFocusIn()
  }
}

const trackFocusOut = async (event: FocusEvent) => {
  try {
    await waitMs(10)
    const clickName = getClickNameOfTarget(event)

    pushFormInputEvent({
      finput_action: 'input_end',
      finput_automatic_focus: false,
      finput_data_clickname: clickName,
      finput_data_container: context?.containerId.value,
      finput_error: getError(),
      finput_firstfocusin: false,
      finput_value: getValue(),
    })
  } catch (error) {
    log('warn', {
      component: 'LcFormField.vue',
      error,
      message: 'pushFormInputEvent failed',
    })
  }
}

// listener is removed inside plugin
onMounted(setTabListener)
</script>
